const HOME_PATH = '/home';
const NEW_BOOKING_PATH = '/bookings/new';
const TIRE_OFFER_PATH = '/tire-offer';
const RECALL_PATH = '/recall';
const BOOKING_PROPOSAL_PATH = '/p';

const appRoutesKeys = {
  home: 'home',
  myCarDetails: 'myCarDetails',
  myCarWheelMeasurements: 'myCarWheelMeasurements',
  discounts: 'discounts',
  myBookingDetails: 'myBookingDetails',
  login: 'login',
  confirmation: 'confirmation',
  homeError: 'homeError',
  profile: 'profile',
  editProfile: 'editProfile',
  bookings: 'bookings',
  myMembershipPrograms: 'myMembershipPrograms',
  myReferrals: 'myReferrals',
  tireHotel: 'tireHotel',
  tireOfferHomePage: 'tireOfferHomePage',
  membership: 'membership',
  paymentInfo: 'paymentInfo',
  referralsInfo: 'referralsInfo',
  campaignsInfo: 'campaignsInfo',
  bookingInfo: 'bookingInfo',
  editMyBookingTimeWindow: 'editMyBookingTimeWindow',
  editBookingAddress: 'editBookingAddress',
  homepage: 'homepage',
  companySignup: 'companySignup',
  companySignupSuccess: 'companySignupSuccess',
  tireOffer: 'tireOffer',
  tireOfferInfo: 'tireOfferInfo',
  tireOfferSelection: 'tireOfferSelection',
  tireOfferSummary: 'tireOfferSummary',
  tireOfferConfirmation: 'tireOfferConfirmation',
  tireOfferPaymentInfo: 'tireOfferPaymentInfo',
  newBooking: 'newBooking',
  newBookingAddress: 'newBookingAddress',
  newBookingCars: 'newBookingCars',
  newBookingPrimaryItems: 'newBookingPrimaryItems',
  newBookingCancelTireHotel: 'newBookingCancelTireHotel',
  newBookingAddons: 'newBookingAddons',
  newBookingTireHotel: 'newBookingTireHotel',
  newBookingTimeWindow: 'newBookingTimeWindow',
  newBookingSummary: 'newBookingSummary',
  newBookingBuilding: 'newBookingBuilding',
  recall: 'recall',
  recallTimeWindowPicker: 'recallTimeWindowPicker',
  recallSummary: 'recallSummary',
  recallLandingPage: 'recallLandingPage',
  recallAddress: 'recallAddress',
  cancelledBooking: 'cancelledBooking',
  cancelledBookingFeedback: 'cancelledBookingFeedback',
  fleetTireHotel: 'fleetTireHotel',
  tireOfferStatus: 'tireOfferStatus',
  bookingProposal: 'bookingProposal',
  bookingProposalLandingPage: 'bookingProposalLandingPage',
  bookingProposalAddress: 'bookingProposalAddress',
  bookingProposalTimeWindowPicker: 'bookingProposalTimeWindowPicker',
  bookingProposalConfirmation: 'bookingProposalConfirmation'
} as const;

type BaseRouteKeys = keyof typeof appRoutesKeys;

type StringOrNumber = string | number;

// Define the parameters for the routes
interface PathConfig {
  myBookingDetails: { id: StringOrNumber };
  expandBookingSummary: { id: StringOrNumber };
  editMyBookingTimeWindow: { id: StringOrNumber };
  editBookingAddress: { id: StringOrNumber };
  myCarDetails: { carId: StringOrNumber };
  myCarWheelMeasurements: { carId: StringOrNumber };
  paymentInfo: { slug: StringOrNumber };
  referralsInfo: { slug: StringOrNumber };
  campaignsInfo: { slug: StringOrNumber };
  bookingInfo: { slug: StringOrNumber };
  confirmation: { slug: StringOrNumber };
  tireOfferInfo: { slug: StringOrNumber };
  tireOfferSelection: { slug: StringOrNumber };
  tireOfferSummary: { slug: StringOrNumber };
  tireOfferConfirmation: { sessionId: string };
  tireOfferPaymentInfo: { sessionId: string };
  recallLandingPage: { slug: StringOrNumber };
  tireOfferStatus: { slug: StringOrNumber };
  bookingProposalLandingPage: { slug: StringOrNumber };
  bookingProposalAddress: { slug: StringOrNumber };
  bookingProposalTimeWindowPicker: { slug: StringOrNumber };
  bookingProposalConfirmation: { slug: StringOrNumber };
  bookingProposal: { slug: StringOrNumber };
}

type InputData<Type extends BaseRouteKeys> = Type extends keyof PathConfig ? PathConfig[Type] : void;

type InputDataOrVoid<Type extends BaseRouteKeys> = InputData<Type> extends void ? void : InputData<Type>;

type RouteDefinition<key extends BaseRouteKeys> = {
  getPath(data: InputDataOrVoid<key>): string;
  getBasePath(): string;
};
export const customerRoutes: {
  [key in BaseRouteKeys]: RouteDefinition<key>;
} = {
  login: {
    getPath: () => '/login',
    getBasePath: () => '/login'
  },
  homepage: {
    getPath: () => `/homepage`,
    getBasePath: () => '/homepage'
  },
  home: {
    getPath: () => `${HOME_PATH}`,
    getBasePath: () => `${HOME_PATH}/*`
  },
  tireOfferInfo: {
    getPath: ({ slug }) => `${TIRE_OFFER_PATH}/info/${slug}`,
    getBasePath: () => `/info/:slug`
  },
  recall: {
    getPath: () => `${RECALL_PATH}/*`,
    getBasePath: () => `${RECALL_PATH}/*`
  },
  recallLandingPage: {
    getPath: ({ slug }) => `${RECALL_PATH}/${slug}`,
    getBasePath: () => '/:slug'
  },
  // style set in homeLayout and routerLayout based on "time-window" in url
  recallTimeWindowPicker: {
    getPath: () => `${RECALL_PATH}/time-window`,
    getBasePath: () => '/time-window'
  },
  recallAddress: {
    getPath: () => `${RECALL_PATH}/address`,
    getBasePath: () => '/address'
  },
  recallSummary: {
    getPath: () => `${RECALL_PATH}/summary`,
    getBasePath: () => '/summary'
  },
  myBookingDetails: {
    getPath: ({ id }) => `${HOME_PATH}/bookings/${id}`,
    getBasePath: () => '/bookings/:id'
  },
  // style set in homeLayout and routerLayout based on "time-window" in url
  editMyBookingTimeWindow: {
    getPath: ({ id }) => `${HOME_PATH}/bookings/${id}/edit-time-window`,
    getBasePath: () => '/bookings/:id/edit-time-window'
  },
  editBookingAddress: {
    getPath: ({ id }) => `${HOME_PATH}/bookings/${id}/edit-address`,
    getBasePath: () => '/bookings/:id/edit-address'
  },
  myCarDetails: {
    getPath: ({ carId }) => `${HOME_PATH}/cars/${carId}`,
    getBasePath: () => '/cars/:carId'
  },
  myCarWheelMeasurements: {
    getPath: ({ carId }) => `${HOME_PATH}/cars/${carId}/wheel-measurements`,
    getBasePath: () => '/cars/:carId/wheel-measurements'
  },
  discounts: {
    getPath: () => `${HOME_PATH}/discounts`,
    getBasePath: () => '/discounts'
  },
  homeError: {
    getPath: () => `${HOME_PATH}/error`,
    getBasePath: () => '/error'
  },
  tireOfferHomePage: {
    getPath: () => `${HOME_PATH}/tire-offer `,
    getBasePath: () => '/tire-offer'
  },
  profile: {
    getPath: () => `${HOME_PATH}/profile`,
    getBasePath: () => '/profile'
  },
  editProfile: {
    getPath: () => `${HOME_PATH}/profile/edit`,
    getBasePath: () => '/profile/edit'
  },
  myMembershipPrograms: {
    getPath: () => `${HOME_PATH}/membership-programs`,
    getBasePath: () => '/membership-programs'
  },
  bookings: {
    getPath: () => `${HOME_PATH}/bookings`,
    getBasePath: () => '/bookings'
  },
  myReferrals: {
    getPath: () => `${HOME_PATH}/referrals`,
    getBasePath: () => '/referrals'
  },
  confirmation: {
    getPath: ({ slug }) => `/confirmation/${slug}`,
    getBasePath: () => '/confirmation/:slug'
  },
  tireHotel: {
    getPath: () => `${HOME_PATH}/tire-hotel`,
    getBasePath: () => '/tire-hotel'
  },
  membership: {
    getPath: () => `/memberships/:slug`,
    getBasePath: () => '/memberships/:slug'
  },
  paymentInfo: {
    getPath: ({ slug }) => `/b/payment/${slug}`,
    getBasePath: () => '/b/payment/:slug'
  },
  referralsInfo: {
    getPath: ({ slug }) => `/referrals/${slug}`,
    getBasePath: () => '/referrals/:slug'
  },
  campaignsInfo: {
    getPath: ({ slug }) => `/campaigns/${slug}`,
    getBasePath: () => '/campaigns/:slug'
  },
  bookingInfo: {
    getPath: ({ slug }) => `/b/${slug}`,
    getBasePath: () => '/b/:slug'
  },
  newBooking: {
    getPath: () => `${NEW_BOOKING_PATH}`,
    getBasePath: () => `${NEW_BOOKING_PATH}/*`
  },
  newBookingAddress: {
    getPath: () => `${NEW_BOOKING_PATH}/address`,
    getBasePath: () => `/address`
  },
  newBookingCars: {
    getPath: () => `${NEW_BOOKING_PATH}/cars`,
    getBasePath: () => `/cars`
  },
  newBookingPrimaryItems: {
    getPath: () => `${NEW_BOOKING_PATH}/services`,
    getBasePath: () => `/services`
  },
  newBookingCancelTireHotel: {
    getPath: () => `${NEW_BOOKING_PATH}/cancel-tire-hotel`,
    getBasePath: () => `/cancel-tire-hotel`
  },
  newBookingAddons: {
    getPath: () => `${NEW_BOOKING_PATH}/addons`,
    getBasePath: () => '/addons'
  },
  newBookingTireHotel: {
    getPath: () => `${NEW_BOOKING_PATH}/tire-hotel-pickup`,
    getBasePath: () => '/tire-hotel-pickup'
  },
  // style set in homeLayout and routerLayout based on "time-window" in url
  newBookingTimeWindow: {
    getPath: () => `${NEW_BOOKING_PATH}/time-window`,
    getBasePath: () => '/time-window'
  },
  newBookingSummary: {
    getPath: () => `${NEW_BOOKING_PATH}/summary`,
    getBasePath: () => '/summary'
  },
  newBookingBuilding: {
    getPath: () => `${NEW_BOOKING_PATH}/building`,
    getBasePath: () => '/building'
  },
  companySignup: {
    getPath: () => '/company-signup',
    getBasePath: () => '/company-signup'
  },
  companySignupSuccess: {
    getPath: () => '/company-signup/success',
    getBasePath: () => '/company-signup/success'
  },
  tireOffer: {
    getPath: () => `${TIRE_OFFER_PATH}/*`,
    getBasePath: () => `${TIRE_OFFER_PATH}/*`
  },

  tireOfferSelection: {
    getPath: ({ slug }) => `${TIRE_OFFER_PATH}/select/${slug}`,
    getBasePath: () => `/select/:slug`
  },
  tireOfferSummary: {
    getPath: ({ slug }) => `${TIRE_OFFER_PATH}/summary/${slug}`,
    getBasePath: () => `/summary/:slug`
  },
  tireOfferConfirmation: {
    getPath: ({ sessionId }) => `${TIRE_OFFER_PATH}/confirmation/${sessionId}`,
    getBasePath: () => '/confirmation/:sessionId'
  },
  tireOfferPaymentInfo: {
    getPath: ({ sessionId }) => `${TIRE_OFFER_PATH}/payment-info/${sessionId}`,
    getBasePath: () => '/payment-info/:sessionId'
  },
  cancelledBooking: {
    getPath: () => `/bookings/cancelled`,
    getBasePath: () => '/bookings/cancelled'
  },
  cancelledBookingFeedback: {
    getPath: () => `/bookings/cancelled/feedback-completed`,
    getBasePath: () => '/bookings/cancelled/feedback-completed'
  },
  fleetTireHotel: {
    getPath: () => `${HOME_PATH}/fleet-tire-hotel`,
    getBasePath: () => 'fleet-tire-hotel'
  },
  tireOfferStatus: {
    getPath: ({ slug }) => `/t/${slug}`,
    getBasePath: () => '/t/:slug'
  },
  bookingProposal: {
    getPath: ({ slug }) => `${BOOKING_PROPOSAL_PATH}/${slug}/*`,
    getBasePath: () => `${BOOKING_PROPOSAL_PATH}/:slug/*`
  },
  bookingProposalLandingPage: {
    getPath: ({ slug }) => `${BOOKING_PROPOSAL_PATH}/${slug}`,
    getBasePath: () => `/`
  },
  bookingProposalAddress: {
    getPath: ({ slug }) => `${BOOKING_PROPOSAL_PATH}/${slug}/address`,
    getBasePath: () => `/address`
  },
  bookingProposalTimeWindowPicker: {
    getPath: ({ slug }) => `${BOOKING_PROPOSAL_PATH}/${slug}/time-window`,
    getBasePath: () => `/time-window`
  },
  bookingProposalConfirmation: {
    getPath: ({ slug }) => `${BOOKING_PROPOSAL_PATH}/${slug}/confirmation`,
    getBasePath: () => `/confirmation`
  }
};
